import BarChartIcon from "@redotech/redo-icons/bar-chart-01.svg";
import BarChartFilledIcon from "@redotech/redo-icons/bar-chart-01_filled.svg";
import BuildingIcon from "@redotech/redo-icons/building-01.svg";
import BuildingFilledIcon from "@redotech/redo-icons/building-01_filled.svg";
import PackageCheckIcon from "@redotech/redo-icons/package-check.svg";
import PackageCheckFilledIcon from "@redotech/redo-icons/package-check_filled.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import React, { memo } from "react";
import * as styles from "./supplies-onboarding-stepper.module.css";

export type SuppliesOnboardingStep = "setup" | "location" | "inventory";

export const suppliesOnboardingSteps: readonly SuppliesOnboardingStep[] = [
  "setup",
  "location",
  "inventory",
];

const stepLabels: Record<SuppliesOnboardingStep, string> = {
  setup: "Setup",
  location: "Location",
  inventory: "Inventory",
};

const stepIcons: Record<
  SuppliesOnboardingStep,
  {
    outline: (props: any) => React.ReactElement;
    filled: (props: any) => React.ReactElement;
  }
> = {
  setup: { outline: PackageCheckIcon, filled: PackageCheckFilledIcon },
  location: { outline: BuildingIcon, filled: BuildingFilledIcon },
  inventory: { outline: BarChartIcon, filled: BarChartFilledIcon },
};

interface SuppliesOnboardingStepperProps {
  steps: readonly SuppliesOnboardingStep[];
  currentStep: SuppliesOnboardingStep;
}

export const SuppliesOnboardingStepper = memo(
  function SuppliesOnboardingStepper({
    steps,
    currentStep,
  }: SuppliesOnboardingStepperProps) {
    const currentIndex = steps.indexOf(currentStep);

    return (
      <Flex align="center" dir="column" gap="xs">
        <Flex align="center" gap="none">
          {steps.map((step, index) => {
            const isActive = index === currentIndex;
            const isCompleted = index < currentIndex;

            return (
              <React.Fragment key={step}>
                {index > 0 && (
                  <Flex
                    className={classNames(styles.connector, {
                      [styles.connectorCompleted!]: index <= currentIndex,
                    })}
                  />
                )}
                <Flex
                  align="center"
                  className={classNames(styles.circle, {
                    [styles.active!]: isActive,
                    [styles.completed!]: isCompleted,
                    [styles.pending!]: !isActive && !isCompleted,
                  })}
                  justify="center"
                >
                  <RedoIcon
                    className={styles.iconStrokeOverride}
                    color={isActive || isCompleted ? "white" : "quaternary"}
                    Icon={
                      isActive
                        ? stepIcons[step].filled
                        : stepIcons[step].outline
                    }
                    size={12}
                  />
                </Flex>
              </React.Fragment>
            );
          })}
        </Flex>
        <Flex align="center" gap="none">
          {steps.map((step, index) => {
            const isActive = index === currentIndex;

            return (
              <React.Fragment key={step}>
                {index > 0 && <Flex className={styles.connectorSpacer} />}
                <Flex className={styles.labelContainer} justify="center">
                  <Text
                    fontSize="xs"
                    fontWeight={isActive ? "semibold" : "regular"}
                    textColor={isActive ? "primary" : "tertiary"}
                  >
                    {stepLabels[step]}
                  </Text>
                </Flex>
              </React.Fragment>
            );
          })}
        </Flex>
      </Flex>
    );
  },
);
